import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/LegalLayout";
import { legal, has, entityName } from "../data/legal";

const LAST_UPDATED = "25 June 2026";

export default function TermsOfService() {
  const entity = entityName();

  const sections = [
    {
      heading: "Acceptance of these terms",
      body: (
        <p>
          These terms govern your use of this website. By accessing or using the
          site, you agree to these terms. If you do not agree, please do not use
          the site.
        </p>
      ),
    },
    {
      heading: "About us",
      body: (
        <p>
          This website is operated by {entity}
          {has(legal.abn) ? ` (ABN ${legal.abn})` : ""}, a migration support and
          coordination service. We are not a registered migration agent, we
          hold no MARN, and we are not a law firm. We do not provide
          migration advice, legal advice, or legal representation, and we do not
          assess your eligibility. We prepare your documentation, match you with
          a registered migration agent who provides the regulated advice and
          lodgement, and stay involved to keep your matter moving.
        </p>
      ),
    },
    {
      heading: "The website is general information only",
      body: (
        <p>
          Nothing on this site, including our intake form, is migration advice or
          an eligibility assessment. The content is general information only. It
          does not take account of your individual circumstances, and it does not
          tell you which visa to apply for or whether you are eligible. You should
          not act on it without obtaining advice from a registered migration
          agent. See our <Link to="/disclaimer/">Disclaimer</Link> for more.
        </p>
      ),
    },
    {
      heading: "Our service and the registered agent",
      body: (
        <>
          <p>
            Our service is preparation, matching, and coordination. We get your
            documentation application-ready, match you with a registered
            migration agent, act as your single point of contact, and keep your
            matter moving. The registered migration agent provides the migration
            advice, assesses your eligibility, and lodges your application. That
            regulated work is theirs, not ours.
          </p>
          <p>
            The registered migration agent is an independent professional. We do
            not supervise, direct, or control their regulated work, and we are
            not responsible for it. We do not guarantee the agent's advice,
            conduct, diligence, or performance, and matching you with an agent is
            not a promise about the outcome of your matter. Your engagement for
            the regulated work is a separate agreement between you and the agent,
            and the agent provides the OMARA Consumer Guide before that work
            begins.
          </p>
          <p>
            Using this website does not create a client relationship with us. We
            act for you only once we have agreed to do so in a written service
            agreement setting out the scope of our support work and our fee.
          </p>
        </>
      ),
    },
    {
      heading: "Fees",
      body: (
        <p>
          There are separate costs, and we keep them separate so it is clear who
          is paid for what. Our support fee covers our preparation, matching, and
          coordination work and is set out in our written service agreement. The
          registered migration agent's professional fee for the regulated advice
          and lodgement is separate and is set by the agent in their own
          agreement with you. Government Visa Application Charges are set by the
          Department of Home Affairs. Third-party costs (such as skills
          assessments, police checks, health examinations, and translations) are
          also separate. We do not receive government charges or third-party
          costs on the Department's or a provider's behalf.
        </p>
      ),
    },
    {
      heading: "No guarantee of outcome",
      body: (
        <p>
          Visa decisions are made solely by the Department of Home Affairs. We do
          not control and cannot guarantee any outcome, processing time, or that
          you will be invited, sponsored, or granted a visa. Nothing on this site
          is a promise or prediction of a particular result.
        </p>
      ),
    },
    {
      heading: "Your responsibilities",
      body: (
        <p>
          You agree to give us accurate, complete, and current information.
          Migration outcomes depend heavily on the accuracy of the information
          provided, and providing false or misleading information to the
          Department can have serious consequences for which we are not
          responsible.
        </p>
      ),
    },
    {
      heading: "Intellectual property",
      body: (
        <p>
          All content on this site, including text, design, logos, and layout, is
          owned by or licensed to {entity} and is protected by law. You may view
          and print content for your own personal, non-commercial use. You must
          not copy, reproduce, or republish it for any other purpose without our
          permission.
        </p>
      ),
    },
    {
      heading: "Acceptable use",
      body: (
        <p>
          You agree not to use the site unlawfully, to interfere with its
          operation or security, to submit false information, or to attempt to
          gain unauthorised access to any part of it or its underlying systems.
        </p>
      ),
    },
    {
      heading: "Third-party links",
      body: (
        <p>
          The site may link to third-party websites, including government pages,
          for your convenience. We are not responsible for the content, accuracy,
          or availability of those external sites.
        </p>
      ),
    },
    {
      heading: "Disclaimers and limitation of liability",
      body: (
        <p>
          The site is provided on an "as is" basis. To the maximum extent
          permitted by law, and subject to any rights you have under the
          Australian Consumer Law that cannot be excluded, we exclude liability
          for any loss or damage arising from your use of, or reliance on, this
          website or its general information.
        </p>
      ),
    },
    {
      heading: "Privacy",
      body: (
        <p>
          We handle your personal information in accordance with our{" "}
          <Link to="/privacy-policy/">Privacy Policy</Link>, which forms part of
          these terms.
        </p>
      ),
    },
    {
      heading: "Governing law",
      body: (
        <p>
          These terms are governed by the laws of Australia and the state or
          territory in which our practice is based. You submit to the
          non-exclusive jurisdiction of the courts of that place.
        </p>
      ),
    },
    {
      heading: "Changes to these terms",
      body: (
        <p>
          We may update these terms from time to time. The current version is
          always available on this page, with the last-updated date shown above.
        </p>
      ),
    },
    {
      heading: "Contact us",
      body: (
        <p>
          Questions about these terms? Reach {entity} through{" "}
          {has(legal.email) ? (
            <a href={`mailto:${legal.email}`}>{legal.email}</a>
          ) : (
            <Link to="/contact/">our contact page</Link>
          )}
          .
        </p>
      ),
    },
  ];

  return (
    <LegalLayout
      title="Terms of Service"
      path="/terms-of-service/"
      description={`The terms that govern your use of the ${entity} website.`}
      lastUpdated={LAST_UPDATED}
      intro="These terms govern your use of this website. Please read them carefully."
      sections={sections}
    />
  );
}
